import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trash2, CheckCircle, Clock, XCircle, Mail, Phone,
  Calendar, Users, MessageSquare, Search,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format ISO timestamp to "12 Mar 2026, 04:35 PM (IST)"
const formatBookingTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }) + ' IST';
  } catch (_) {
    return iso;
  }
};

// Format YYYY-MM-DD to "15 Dec 2026"
const formatPreferredDate = (yyyyMMdd) => {
  if (!yyyyMMdd) return '—';
  // Parse as local (not UTC) by appending T12:00 to avoid timezone shift
  try {
    const [y, m, day] = yyyyMMdd.split('-');
    if (!y || !m || !day) return yyyyMMdd;
    const d = new Date(parseInt(y), parseInt(m) - 1, parseInt(day));
    return d.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch (_) {
    return yyyyMMdd;
  }
};

const statusStyles = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  confirmed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  cancelled: 'bg-red-100 text-red-700 border-red-300',
};

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | pending | confirmed | cancelled
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Newest first
      const sorted = [...response.data].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setBookings(sorted);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${API}/admin/bookings/${bookingId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update booking');
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Booking deleted');
      fetchBookings();
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Failed to delete booking');
    }
  };

  // Apply filter + search
  const filtered = bookings.filter((b) => {
    if (filter !== 'all' && b.status !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        b.name?.toLowerCase().includes(q) ||
        b.email?.toLowerCase().includes(q) ||
        b.phone?.includes(q) ||
        b.package_name?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const counts = {
    all: bookings.length,
    pending: bookings.filter((b) => b.status === 'pending').length,
    confirmed: bookings.filter((b) => b.status === 'confirmed').length,
    cancelled: bookings.filter((b) => b.status === 'cancelled').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-body text-muted-foreground">Loading bookings...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-bookings">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl text-navy">Booking Management</h1>
          <p className="font-body text-sm text-muted-foreground mt-1">
            {counts.all} total · {counts.pending} pending · {counts.confirmed} confirmed
          </p>
        </div>
      </div>

      {/* Filters + Search */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6 flex flex-wrap gap-3 items-center">
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'confirmed', 'cancelled'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2 rounded-lg font-body text-sm font-medium transition ${
                filter === f
                  ? 'bg-emerald-600 text-white'
                  : 'bg-sand/40 text-navy hover:bg-sand'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}{' '}
              <span className="opacity-70">({counts[f]})</span>
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[200px] relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, email, phone, package…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-border rounded-lg font-body text-sm"
            data-testid="booking-search"
          />
        </div>
      </div>

      {/* Booking cards */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Calendar size={48} className="mx-auto text-emerald-300 mb-4" />
          <p className="font-body text-navy text-lg">No bookings found</p>
          <p className="font-body text-sm text-muted-foreground mt-2">
            {filter !== 'all' || search
              ? 'Try changing your filter or search query.'
              : 'New bookings will appear here as soon as customers book.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((booking) => (
            <div
              key={booking.id}
              className="bg-white rounded-xl shadow-md overflow-hidden border-l-4 border-emerald-500"
              data-testid={`booking-card-${booking.id}`}
            >
              {/* Header strip */}
              <div className="bg-gradient-to-r from-emerald-50 to-sand/30 px-5 py-3 flex flex-wrap items-center justify-between gap-2 border-b border-border">
                <div className="flex items-center space-x-3">
                  <h3 className="font-heading text-lg text-navy">{booking.name}</h3>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${
                      statusStyles[booking.status] || statusStyles.pending
                    }`}
                  >
                    {booking.status}
                  </span>
                  {booking.payment_status === 'completed' && (
                    <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-300">
                      💳 Paid
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2 text-xs font-body text-muted-foreground">
                  <Clock size={14} />
                  <span data-testid={`booking-created-${booking.id}`}>
                    Booked on: <span className="font-semibold text-navy">{formatBookingTime(booking.created_at)}</span>
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4">
                {/* Contact column */}
                <div className="md:col-span-3 space-y-2">
                  <p className="font-accent text-[10px] uppercase tracking-wider text-muted-foreground">Contact</p>
                  <a
                    href={`mailto:${booking.email}`}
                    className="flex items-center space-x-2 text-sm text-navy hover:text-emerald-700"
                  >
                    <Mail size={14} className="text-emerald-600 flex-shrink-0" />
                    <span className="truncate">{booking.email}</span>
                  </a>
                  <a
                    href={`tel:${booking.phone}`}
                    className="flex items-center space-x-2 text-sm text-navy hover:text-emerald-700"
                  >
                    <Phone size={14} className="text-emerald-600 flex-shrink-0" />
                    <span>{booking.phone}</span>
                  </a>
                  <a
                    href={`https://wa.me/${booking.phone?.replace(/[^\d]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-xs text-emerald-700 hover:underline"
                  >
                    <MessageSquare size={12} />
                    <span>WhatsApp</span>
                  </a>
                </div>

                {/* Trip column */}
                <div className="md:col-span-4 space-y-2 md:border-l md:border-border md:pl-4">
                  <p className="font-accent text-[10px] uppercase tracking-wider text-muted-foreground">Trip</p>
                  <p className="font-body text-sm font-semibold text-navy">
                    {booking.package_name}
                  </p>
                  <div className="flex items-center space-x-2 text-sm text-navy">
                    <Calendar size={14} className="text-emerald-600" />
                    <span className="font-semibold" data-testid={`booking-date-${booking.id}`}>
                      {formatPreferredDate(booking.preferred_date)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-navy">
                    <Users size={14} className="text-emerald-600" />
                    <span>{booking.guests} guest{booking.guests !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                {/* Amount column */}
                <div className="md:col-span-2 md:border-l md:border-border md:pl-4">
                  <p className="font-accent text-[10px] uppercase tracking-wider text-muted-foreground">Amount</p>
                  <p className="price-luxe-bold text-2xl text-emerald-700 mt-1">
                    {booking.currency === 'INR' || !booking.currency ? '₹' : booking.currency + ' '}
                    {booking.amount ? booking.amount.toLocaleString() : '—'}
                  </p>
                  <p className="font-body text-[11px] text-muted-foreground uppercase tracking-wider">
                    {booking.currency || 'INR'}
                  </p>
                </div>

                {/* Special Requests column */}
                <div className="md:col-span-3 md:border-l md:border-border md:pl-4">
                  <p className="font-accent text-[10px] uppercase tracking-wider text-muted-foreground flex items-center">
                    <MessageSquare size={11} className="mr-1" /> Special Requests
                  </p>
                  {booking.special_requests && booking.special_requests.trim() ? (
                    <p
                      className="font-body text-sm text-navy bg-amber-50 border-l-2 border-amber-400 pl-3 py-2 mt-1 italic"
                      data-testid={`booking-requests-${booking.id}`}
                    >
                      {booking.special_requests}
                    </p>
                  ) : (
                    <p className="font-body text-xs text-muted-foreground italic mt-1">
                      None provided
                    </p>
                  )}
                </div>
              </div>

              {/* Action footer */}
              <div className="px-5 py-3 bg-sand/20 border-t border-border flex flex-wrap items-center justify-end gap-2">
                {booking.status === 'pending' && (
                  <button
                    onClick={() => updateStatus(booking.id, 'confirmed')}
                    className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-body text-sm font-semibold transition"
                    data-testid={`confirm-${booking.id}`}
                  >
                    <CheckCircle size={16} />
                    <span>Confirm</span>
                  </button>
                )}
                {booking.status === 'pending' && (
                  <button
                    onClick={() => updateStatus(booking.id, 'cancelled')}
                    className="flex items-center space-x-2 bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2 rounded-lg font-body text-sm font-semibold transition border border-red-200"
                  >
                    <XCircle size={16} />
                    <span>Cancel</span>
                  </button>
                )}
                <button
                  onClick={() => handleDelete(booking.id)}
                  className="flex items-center space-x-2 bg-white hover:bg-red-50 text-red-500 px-4 py-2 rounded-lg font-body text-sm font-semibold transition border border-red-200"
                  title="Delete permanently"
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBookings;
